import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Loader2, Plus, UserCheck, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/commercials`;

const STATE_STYLE = {
  en_cours: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  a_venir: 'bg-sky-100 text-sky-700 border-sky-200',
  termine: 'bg-gray-100 text-gray-600 border-gray-200',
};
const STATE_LABEL = { en_cours: 'En cours', a_venir: 'À venir', termine: 'Terminé' };

const EMPTY = { commercialId: '', substituteId: '', startDate: '', endDate: '', reason: '' };

/** Congés & intérims : pendant l'absence, le crédit des nouvelles opérations va au remplaçant. */
export const CommercialInterimsCard = () => {
  const [items, setItems] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: interims }, { data: coms }] = await Promise.all([
        axios.get(`${API}/interims`),
        axios.get(API),
      ]);
      setItems(interims?.items || []);
      setCommercials(coms?.commercials || coms?.items || []);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/interims`, form);
      toast.success('Intérim enregistré');
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => {
    try {
      await axios.delete(`${API}/interims/${id}`);
      toast.success('Intérim annulé — retour du titulaire');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Annulation impossible');
    }
  };

  const names = (c) => c.fullName || c.name
    || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email;
  const valid = form.commercialId && form.substituteId && form.startDate && form.endDate
    && form.reason.trim().length >= 3;

  return (
    <Card data-testid="commercial-interims-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-violet-700" /> Congés & intérims
            </CardTitle>
            <CardDescription>
              Pendant l'absence d'un commercial, les opérations créées sont créditées au
              <strong> remplaçant</strong> tandis que le <strong>portefeuille reste au titulaire</strong>,
              qui le récupère automatiquement à la date de fin. L'historique des commissions déjà
              acquises n'est jamais réécrit.
            </CardDescription>
          </div>
          <Button
            variant={showForm ? 'outline' : 'default'}
            onClick={() => setShowForm(!showForm)}
            data-testid="toggle-interim-form"
          >
            {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {showForm ? 'Annuler' : 'Déclarer une absence'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <div className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2" data-testid="interim-form">
            <div>
              <Label className="text-xs">Commercial absent</Label>
              <Select value={form.commercialId}
                      onValueChange={(v) => setForm({ ...form, commercialId: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="interim-select-titulaire">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {commercials.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{names(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Remplaçant</Label>
              <Select value={form.substituteId}
                      onValueChange={(v) => setForm({ ...form, substituteId: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="interim-select-substitute">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {commercials.filter((c) => c.id !== form.commercialId).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{names(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Du</Label>
              <Input type="date" value={form.startDate} className="h-9 text-sm"
                     onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                     data-testid="interim-start-date" />
            </div>
            <div>
              <Label className="text-xs">Au (inclus)</Label>
              <Input type="date" value={form.endDate} className="h-9 text-sm"
                     onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                     data-testid="interim-end-date" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Motif</Label>
              <Input value={form.reason} placeholder="Congés annuels, mission, formation…"
                     className="h-9 text-sm"
                     onChange={(e) => setForm({ ...form, reason: e.target.value })}
                     data-testid="interim-reason" />
            </div>
            <div className="sm:col-span-2">
              <Button onClick={submit} disabled={!valid || saving} data-testid="interim-submit">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
                Enregistrer l'intérim
              </Button>
            </div>
          </div>
        )}

        {loading && (
          <p className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </p>
        )}

        {!loading && items.length === 0 && (
          <p className="text-sm text-gray-500" data-testid="interims-empty">
            Aucune absence déclarée. Tant qu'aucun intérim n'est en cours, chaque opération est
            créditée au commercial propriétaire de la société cliente.
          </p>
        )}

        {items.map((it) => (
          <div key={it.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-3"
               data-testid={`interim-row-${it.id}`}>
            <Badge variant="outline" className={`text-[10px] ${STATE_STYLE[it.state] || ''}`}>
              {STATE_LABEL[it.state] || it.state}
            </Badge>
            <span className="text-sm font-semibold text-gray-900">{it.commercialName}</span>
            <span className="text-xs text-gray-500">remplacé par</span>
            <span className="text-sm font-semibold text-violet-700">{it.substituteName}</span>
            <span className="text-xs text-gray-500 font-mono">
              {it.startDate} → {it.endDate}
            </span>
            <span className="text-xs text-gray-500">· {it.reason}</span>
            <Button variant="ghost" size="sm" className="ml-auto text-red-600"
                    onClick={() => cancel(it.id)} data-testid={`interim-cancel-${it.id}`}>
              Annuler
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default CommercialInterimsCard;
